// DNABadge — small inline chip surfaced when ≥1 STRIKZA DNA profile
// is active for a fixture+market combination.
//
// Doctrine:
//   - Hidden entirely when no relevant active profile exists. Never
//     invent presence. (`relevantProfilesForMarket` × the read API's
//     `activeProfiles` ∩.)
//   - Tooltip names which profiles fired so the user can mentally
//     attribute the signal.
//   - This is a recommendation surface only. STRIKZA does not place
//     bets; the badge is informational alongside the existing
//     RouteBadge / ValueEdgeBadge family.

import { useMemo } from "react";
import { useDNAProfile } from "../dna/useDNAProfile.ts";
import {
  DNA_PROFILE_LABEL,
  relevantProfilesForMarket,
  type DNAProfileName,
} from "../dna/dna-relevance.ts";

export interface DNABadgeProps {
  readonly fixtureId: string | null | undefined;
  readonly market: string;
  /** Hide the count digit when true. */
  readonly compact?: boolean;
}

export function DNABadge({ fixtureId, market, compact = false }: DNABadgeProps): JSX.Element | null {
  const { bundle, loading } = useDNAProfile(fixtureId ?? null);

  const matched: readonly DNAProfileName[] = useMemo(() => {
    if (!bundle) return [];
    const relevant = new Set<DNAProfileName>(relevantProfilesForMarket(market));
    return bundle.activeProfiles.filter((p) => relevant.has(p));
  }, [bundle, market]);

  if (loading) return null;
  if (matched.length === 0) return null;

  const tooltip =
    "STRIKZA DNA active: " +
    matched.map((p) => DNA_PROFILE_LABEL[p] ?? p).join(", ");

  return (
    <span
      title={tooltip}
      aria-label={tooltip}
      style={{
        fontFamily: "var(--font-mono, monospace)",
        fontSize: 10,
        letterSpacing: "0.5px",
        textTransform: "uppercase",
        color: "var(--accent, #b07cff)",
        background: "var(--accent-soft, rgba(176,124,255,0.10))",
        padding: "2px 6px",
        border: "1px solid var(--accent, #b07cff)",
        borderRadius: 4,
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        whiteSpace: "nowrap",
      }}
    >
      <span aria-hidden="true">DNA</span>
      {compact ? null : (
        <span aria-hidden="true" style={{ opacity: 0.85, fontSize: 9 }}>
          ×{matched.length}
        </span>
      )}
    </span>
  );
}
